import { computed } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import {
  getTFACurtailmentIntervals,
  createTFACurtailmentInterval,
  updateTFACurtailmentInterval,
  deleteTFACurtailmentInterval,
  getTFACurtailmentIntervalsRatio,
} from '@/services/cpm/cpmProgramManagement.service'
import type { ITFACurtailmentIntervalCreationPayload } from '@/types/cpm'
import type { ITFACurtailmentIntervalFilters, ITFACurtailmentIntervalRatioFilters } from '@/types/cpm/filters'
import type { ComputedRef } from 'vue'
import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'

const TFA_CURTAILMENT_INTERVALS_QUERY_KEY = ['cpm', 'tfa-curtailment-intervals']

export function useTFACurtailmentIntervals() {
  const queryClient = useQueryClient()

  const getTFACurtailmentIntervalsQuery = (filters: ComputedRef<ITFACurtailmentIntervalFilters>) => {
    return useQuery({
      queryKey: [...TFA_CURTAILMENT_INTERVALS_QUERY_KEY, filters],
      queryFn: async () => getTFACurtailmentIntervals(filters.value),
      enabled: computed(() => !!filters.value.site_id),
    })
  }

  const createTFACurtailmentIntervalMutation = useMutation({
    mutationFn: (payload: ITFACurtailmentIntervalCreationPayload) => createTFACurtailmentInterval(payload),
    onSuccess: () => {
      NotifySuccess('Curtailment interval created successfully.')
      queryClient.invalidateQueries({ queryKey: TFA_CURTAILMENT_INTERVALS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to create curtailment interval'),
  })

  const updateTFACurtailmentIntervalMutation = useMutation({
    mutationFn: ({ id, curtailed }: { id: number; curtailed: boolean }) => updateTFACurtailmentInterval(id, curtailed),
    onSuccess: () => {
      NotifySuccess('Curtailment interval updated successfully.')
      queryClient.invalidateQueries({ queryKey: TFA_CURTAILMENT_INTERVALS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to update curtailment interval'),
  })

  const deleteTFACurtailmentIntervalMutation = useMutation({
    mutationFn: (id: number) => deleteTFACurtailmentInterval(id),
    onSuccess: () => {
      NotifySuccess('Curtailment interval deleted successfully.')
      queryClient.invalidateQueries({ queryKey: TFA_CURTAILMENT_INTERVALS_QUERY_KEY })
    },
    onError: NotifyErrorWrapper('Failed to delete curtailment interval'),
  })

  const getCurtailmentRatioQuery = (filters: ComputedRef<ITFACurtailmentIntervalRatioFilters>) =>
    useQuery({
      queryKey: [...TFA_CURTAILMENT_INTERVALS_QUERY_KEY, 'curtailment-ratio', filters],
      queryFn: async () => getTFACurtailmentIntervalsRatio(filters.value),
      enabled: computed(() => !!filters.value.site_id && !!filters.value.year && !!filters.value.month),
    })

  return {
    getTFACurtailmentIntervalsQuery,
    getCurtailmentRatioQuery,
    createTFACurtailmentIntervalMutation,
    updateTFACurtailmentIntervalMutation,
    deleteTFACurtailmentIntervalMutation,
  }
}
